import { RT4K_FILE_PATH_BYTES, rt4kArrayElementOffset, rt4kFieldOffset } from './layout';

export type SettingType =
  | 'enum'
  | 'bit'
  | 'uint8'
  | 'int8'
  | 'uint16'
  | 'int16'
  | 'uint32'
  | 'int32'
  | 'float32'
  | 'string';

export interface ByteRange {
  address: number;
  length: number;
}

export interface SettingOption {
  label: string;
  value: number[];
}

export interface SettingDefinition {
  path: string;
  label: string;
  category: 'Input' | 'HDMI Output' | 'Acquisition' | 'Processing' | 'System';
  description: string;
  byteRanges: ByteRange[];
  type: SettingType;
  writable: boolean;
  options?: SettingOption[];
  min?: number;
  max?: number;
  step?: number;
  scale?: number;
  offset?: number;
  precision?: number;
  units?: string;
  preserveTrailingZeros?: boolean;
  showPositiveSign?: boolean;
  showPositiveSignForZero?: boolean;
}

export const RT4K_SCHEMA_VERSION = '1.80.0-known-offsets';
export const AUDIO_RESAMPLER_PATH = 'advanced.acquisition.audio_input.asrc_resampler.target_freq';
export const DEBLUR_PATH = 'advanced.acquisition.sample_rate_detection.control.deblur_decimation_8_4';
export const LUMACODE_PRESET_PATH = 'advanced.acquisition.sample_rate_detection.lumacode.load_palette';

function option(label: string, value: number | number[]): SettingOption {
  return { label, value: Array.isArray(value) ? value : [value] };
}

function byteRange(address: number, length = 1): ByteRange[] {
  return [{ address, length }];
}

function enumSetting(
  path: string,
  label: string,
  category: SettingDefinition['category'],
  description: string,
  address: number,
  options: SettingOption[],
): SettingDefinition {
  return {
    path,
    label,
    category,
    description,
    byteRanges: byteRange(address),
    type: 'enum',
    writable: true,
    options,
  };
}

function bitSetting(
  path: string,
  label: string,
  category: SettingDefinition['category'],
  description: string,
  address: number,
): SettingDefinition {
  return {
    path,
    label,
    category,
    description,
    byteRanges: byteRange(address),
    type: 'bit',
    writable: true,
  };
}

function numericSetting(
  path: string,
  label: string,
  category: SettingDefinition['category'],
  description: string,
  address: number,
  type: Extract<SettingType, 'uint8' | 'int8' | 'uint16' | 'int16' | 'uint32' | 'int32' | 'float32'>,
  settings: Pick<
    SettingDefinition,
    'min' | 'max' | 'step' | 'scale' | 'offset' | 'precision' | 'units' | 'preserveTrailingZeros' | 'showPositiveSign' | 'showPositiveSignForZero'
  > = {},
): SettingDefinition {
  return {
    path,
    label,
    category,
    description,
    byteRanges: byteRange(address, type.endsWith('32') ? 4 : type.endsWith('16') ? 2 : 1),
    type,
    writable: true,
    ...settings,
  };
}

export const SETTING_DEFINITIONS: SettingDefinition[] = [
  {
    path: 'input',
    label: 'Input',
    category: 'Input',
    description: 'Input source',
    byteRanges: [
      { address: 0x0368, length: 1 },
      { address: 0x5869, length: 5 },
    ],
    type: 'enum',
    writable: true,
    options: [
      { label: 'HDMI', value: [5, 0, 2, 4, 0x88, 0x58] },
      { label: 'Front|Composite', value: [3, 3, 1, 3, 0xb8, 0x5d] },
      { label: 'Front|S-Video', value: [3, 4, 1, 3, 0xb8, 0x5d] },
      { label: 'RCA|YPbPr', value: [0, 7, 0, 1, 0xa4, 0x5b] },
      { label: 'RCA|RGsB', value: [0, 8, 0, 1, 0xa4, 0x5b] },
      { label: 'RCA|CVBS on Green', value: [0, 9, 1, 1, 0xb8, 0x5d] },
      { label: 'SCART|RGBS (75 Ohm)', value: [2, 12, 0, 2, 0xa4, 0x5b] },
      { label: 'SCART|RGsB', value: [2, 13, 0, 2, 0xa4, 0x5b] },
      { label: 'SCART|YPbPr', value: [2, 14, 0, 2, 0xa4, 0x5b] },
      { label: 'SCART|CVBS on Pin 20', value: [2, 15, 1, 2, 0xb8, 0x5d] },
      { label: 'SCART|CVBS on Green', value: [2, 16, 1, 2, 0xb8, 0x5d] },
      { label: 'SCART|Y/C on Pin 20/Red', value: [2, 17, 1, 2, 0xb8, 0x5d] },
      { label: 'HD-15|RGBHV', value: [1, 20, 0, 0, 0xa4, 0x5b] },
      { label: 'HD-15|RGBS', value: [1, 21, 0, 0, 0xa4, 0x5b] },
      { label: 'HD-15|RGsB', value: [1, 22, 0, 0, 0xa4, 0x5b] },
      { label: 'HD-15|YPbPr', value: [1, 23, 0, 0, 0xa4, 0x5b] },
      { label: 'HD-15|CVBS on Hsync', value: [1, 24, 1, 0, 0xb8, 0x5d] },
      { label: 'HD-15|CVBS on Green', value: [1, 25, 1, 0, 0xb8, 0x5d] },
      { label: 'HD-15|Y/C on Green/Red', value: [1, 26, 1, 0, 0xb8, 0x5d] },
      { label: 'HD-15|Y/C on G/R (Enh.)', value: [1, 27, 0, 0, 0xa4, 0x5b] },
    ],
  },
  {
    path: 'output.resolution',
    label: 'Resolution',
    category: 'HDMI Output',
    description: 'HDMI output resolution',
    byteRanges: [{ address: 0x036c, length: 1 }],
    type: 'enum',
    writable: true,
    options: [
      { label: '4K60', value: [0] },
      { label: '4K50', value: [1] },
      { label: '1080p60', value: [2] },
      { label: '1080p50', value: [3] },
      { label: '1440p60', value: [4] },
      { label: '1440p50', value: [5] },
      { label: '1080p100', value: [6] },
      { label: '1440p100', value: [7] },
      { label: '1080p120', value: [8] },
      { label: '1440p120', value: [9] },
      { label: '480p60', value: [13] },
      { label: 'Custom 1', value: [69] },
      { label: 'Custom 2', value: [70] },
      { label: 'Custom 3', value: [71] },
      { label: 'Custom 4', value: [72] },
    ],
  },
  {
    path: 'output.custom_modelines.custom_3',
    label: 'Custom 3 Modeline',
    category: 'HDMI Output',
    description: 'Custom 3 output modeline file path',
    byteRanges: [{ address: rt4kArrayElementOffset('custom_slot_file', 0), length: RT4K_FILE_PATH_BYTES }],
    type: 'string',
    writable: false,
  },
  {
    path: 'output.custom_modelines.custom_4',
    label: 'Custom 4 Modeline',
    category: 'HDMI Output',
    description: 'Custom 4 output modeline file path',
    byteRanges: [{ address: rt4kArrayElementOffset('custom_slot_file', 1), length: RT4K_FILE_PATH_BYTES }],
    type: 'string',
    writable: false,
  },
  {
    path: 'output.transmitter.hdr',
    label: 'HDR',
    category: 'HDMI Output',
    description: 'HDR transmitter mode',
    byteRanges: [{ address: 0x02d0, length: 1 }],
    type: 'enum',
    writable: true,
    options: [
      { label: 'Off', value: [0] },
      { label: 'HDR10 [8-bit]', value: [1] },
      { label: 'HLG [8-bit]', value: [2] },
    ],
  },
  {
    path: 'output.transmitter.deep_color',
    label: 'Deep Color',
    category: 'HDMI Output',
    description: '10-bit output',
    byteRanges: [{ address: 0x02d4, length: 1 }],
    type: 'bit',
    writable: true,
  },
  {
    path: 'output.transmitter.sync_lock',
    label: 'Sync Lock',
    category: 'HDMI Output',
    description: 'Output sync lock mode',
    byteRanges: [{ address: 0x02d8, length: 1 }],
    type: 'enum',
    writable: true,
    options: [
      { label: 'Adaptive Triple Buffer', value: [4] },
      { label: 'Gen Lock', value: [1] },
      { label: 'Frame Lock', value: [2] },
      { label: 'TBC', value: [3] },
      { label: 'Triple Buffer', value: [0] },
    ],
  },
  {
    path: 'output.transmitter.vrr',
    label: 'VRR',
    category: 'HDMI Output',
    description: 'Variable refresh rate mode',
    byteRanges: [{ address: 0x02dc, length: 1 }],
    type: 'enum',
    writable: true,
    options: [
      { label: 'Off', value: [0] },
      { label: 'FreeSync', value: [1] },
      { label: 'VESA', value: [2] },
    ],
  },
  {
    path: 'output.transmitter.colorimetry',
    label: 'Colorimetry',
    category: 'HDMI Output',
    description: 'HDMI colorimetry metadata',
    byteRanges: [{ address: 0x1ec8, length: 1 }],
    type: 'enum',
    writable: true,
    options: [
      { label: 'Auto (Rec. 709)', value: [0] },
      { label: 'Rec.709', value: [1] },
      { label: 'Rec.2020', value: [2] },
      { label: 'Adobe RGB', value: [3] },
      { label: 'Display-P3', value: [4] },
    ],
  },
  {
    path: 'output.transmitter.rgb_range',
    label: 'RGB Range',
    category: 'HDMI Output',
    description: 'HDMI RGB range',
    byteRanges: [{ address: 0x1f08, length: 1 }],
    type: 'enum',
    writable: true,
    options: [
      { label: 'Full', value: [0] },
      { label: 'Limited', value: [1] },
    ],
  },
  {
    path: 'profiles.load_profile.auto_load_dv1',
    label: 'Auto Load DV1',
    category: 'System',
    description: 'Auto-load DV1 profile',
    byteRanges: [{ address: rt4kFieldOffset('auto_dv1_profile'), length: 4 }],
    type: 'enum',
    writable: true,
    options: [
      option('Off', [0, 0, 0, 0]),
      option('On', [1, 0, 0, 0]),
    ],
  },
  {
    path: 'profiles.load_profile.auto_load_svs',
    label: 'Auto Load SVS',
    category: 'System',
    description: 'Auto-load SVS profile',
    byteRanges: [{ address: rt4kFieldOffset('auto_svs_profile'), length: 4 }],
    type: 'enum',
    writable: true,
    options: [
      option('Off', [0, 0, 0, 0]),
      option('On', [1, 0, 0, 0]),
    ],
  },
  {
    path: 'profiles.load_profile.created_on_device_id',
    label: 'Created On Device ID',
    category: 'System',
    description: 'Device ID that created or last saved this profile',
    byteRanges: [{ address: rt4kFieldOffset('profile_created_on'), length: 8 }],
    type: 'string',
    writable: true,
  },
  numericSetting('advanced.acquisition.audio_input.sampling.preamp_balance', 'Pre-amp Balance', 'Acquisition', 'Audio input pre-amp balance', 0x1f48, 'int8', {
    min: -24,
    max: 24,
    step: 0.5,
    scale: 0.5,
    precision: 1,
  }),
  numericSetting('advanced.acquisition.audio_input.sampling.preamp_gain', 'Pre-amp Gain', 'Acquisition', 'Audio input pre-amp gain', 0x1610, 'int8', {
    min: -24,
    max: 28,
    step: 0.5,
    scale: 0.5,
    precision: 1,
    units: 'dB',
    preserveTrailingZeros: true,
    showPositiveSign: true,
  }),
  enumSetting('advanced.acquisition.audio_input.sampling.sample_rate', 'Sample Rate', 'Acquisition', 'Audio input sample rate', 0x1624, [
    option('48 kHz', 0),
    option('96 kHz', 1),
    option('192 kHz', 2),
    option('384 kHz', 3),
  ]),
  enumSetting(AUDIO_RESAMPLER_PATH, 'Target Freq', 'Acquisition', 'ASRC resampler target frequency; lock status is reported separately by the device', rt4kFieldOffset('resampler_mode'), [
    option('Off', 0),
    option('48 kHz', 1),
    option('96 kHz', 2),
  ]),
  enumSetting('advanced.acquisition.audio_input.source.input_override', 'Input Override', 'Acquisition', 'Audio input override', 0x1618, [
    option('Off', 0),
    option('RCA', 1),
    option('HD-15', 2),
    option('SCART', 3),
    option('Front', 4),
    option('S/PDIF', 5),
  ]),
  enumSetting('advanced.acquisition.audio_input.source.input_swap', 'Input Swap', 'Acquisition', 'Audio input swap', 0x1620, [
    option('Off', 0),
    option('Mono (Left)', 1),
    option('Mono (Right)', 2),
    option('L/R Swap', 3),
  ]),
  enumSetting('advanced.acquisition.hdmi_receiver.adac.enable', 'A/DAC Enable', 'Acquisition', 'HDMI receiver A/DAC enable', 0x1eec, [
    option('Off', 0),
    option('NTSC', 1),
    option('PAL', 2),
  ]),
  enumSetting('advanced.acquisition.hdmi_receiver.adac.auto_decimate', 'A/DAC Auto-Decimate', 'Acquisition', 'HDMI receiver A/DAC auto-decimate', 0x1f10, [
    option('On', 0),
    option('Off', 1),
  ]),
  enumSetting('advanced.acquisition.hdmi_receiver.colorspace.upsampler_422', '4:2:2 Upsampler', 'Acquisition', 'HDMI receiver 4:2:2 upsampler', 0x1600, [
    option('Linear', 0),
    option('Nearest', 1),
  ]),
  enumSetting('advanced.acquisition.hdmi_receiver.colorspace.input_range', 'Input Range', 'Acquisition', 'HDMI receiver input range', 0x1604, [
    option('Auto', 0),
    option('RGB Lim.', 1),
    option('RGB Full', 2),
    option('YCbCr 601', 3),
    option('YCbCr 709', 4),
    option('XVYCC 601', 5),
    option('XVYCC 709', 6),
    option('YCbCr 601 (Full)', 7),
    option('YCbCr 709 (Full)', 8),
  ]),
  enumSetting('advanced.acquisition.hdmi_receiver.dv1.auto_decimate', 'DV1 Auto-Decimate', 'Acquisition', 'MiSTer DV1 auto-decimate', 0x1ef8, [
    option('Infoframe', 0),
    option('Measure', 1),
    option('Off', 2),
  ]),
  enumSetting('advanced.acquisition.hdmi_receiver.dv1.auto_crop', 'DV1 Auto-Crop', 'Acquisition', 'MiSTer DV1 auto-crop', 0x1efc, [
    option('On', 0),
    option('Off', 1),
  ]),
  enumSetting('advanced.acquisition.hdmi_receiver.dv1.auto_rotate', 'DV1 Auto-RoTATE', 'Acquisition', 'MiSTer DV1 auto-rotate', 0x1f64, [
    option('On', 0),
    option('Off', 1),
  ]),
  enumSetting('advanced.acquisition.hdmi_receiver.dv1.target_decimate', 'DV1 Target Decimate', 'Acquisition', 'MiSTer DV1 target decimation', 0x1f50, [
    option('Off', 0),
    option('720', 1),
    option('1024', 2),
    option('1280', 3),
    option('1440', 4),
    option('1600', 5),
  ]),
  numericSetting('advanced.acquisition.hdmi_receiver.input_decimation.input_pixels', 'Input Pixels', 'Acquisition', 'HDMI input decimation input pixels', 0x0ee9, 'uint8', {
    min: 1,
    max: 63,
    step: 1,
  }),
  numericSetting('advanced.acquisition.hdmi_receiver.input_decimation.initial_phase', 'Initial Phase', 'Acquisition', 'HDMI input decimation initial phase', 0x0f69, 'uint8', {
    min: 1,
    max: 63,
    step: 1,
    offset: 1,
  }),
  numericSetting('advanced.acquisition.hdmi_receiver.input_decimation.output_pixels', 'Output Pixels', 'Acquisition', 'HDMI input decimation output pixels', 0x18f9, 'uint8', {
    min: 1,
    max: 63,
    step: 1,
  }),
  bitSetting('advanced.acquisition.hdmi_receiver.ntsc_3d.comb_enable', '3D Comb Enable', 'Acquisition', 'HDMI receiver NTSC 3D comb enable', 0x1f6c),
  enumSetting('advanced.acquisition.hdmi_receiver.ntsc_3d.sensitivity', '3D Sensitivity', 'Acquisition', 'HDMI receiver NTSC 3D sensitivity', 0x1f6e, [
    option('Default', 0),
    option('Low', 1),
    option('Medium', 2),
    option('High', 3),
  ]),
  numericSetting('advanced.acquisition.rgb_adc.gain.pre_adc', 'Pre-ADC Gain', 'Acquisition', 'RGB/component pre-ADC gain', 0x11fc, 'int16', {
    min: -0.7,
    max: 0.8,
    step: 0.1,
    scale: 0.1,
    precision: 1,
    preserveTrailingZeros: true,
    showPositiveSign: true,
  }),
  numericSetting('advanced.acquisition.rgb_adc.gain.red', 'ADC Red Gain', 'Acquisition', 'RGB/component red gain', 0x12fc, 'int16', {
    min: 0,
    max: 1.996,
    step: 1 / 256,
    scale: 1 / 256,
    offset: 1,
    precision: 3,
    preserveTrailingZeros: true,
  }),
  numericSetting('advanced.acquisition.rgb_adc.gain.green', 'ADC Green Gain', 'Acquisition', 'RGB/component green gain', 0x13fc, 'int16', {
    min: 0,
    max: 1.996,
    step: 1 / 256,
    scale: 1 / 256,
    offset: 1,
    precision: 3,
    preserveTrailingZeros: true,
  }),
  numericSetting('advanced.acquisition.rgb_adc.gain.blue', 'ADC Blue Gain', 'Acquisition', 'RGB/component blue gain', 0x14fc, 'int16', {
    min: 0,
    max: 1.996,
    step: 1 / 256,
    scale: 1 / 256,
    offset: 1,
    precision: 3,
    preserveTrailingZeros: true,
  }),
  numericSetting('advanced.acquisition.rgb_adc.offset.red', 'ADC Red Offset', 'Acquisition', 'RGB/component red offset', 0x1930, 'int16', {
    min: -100,
    max: 100,
    step: 1,
    showPositiveSign: true,
  }),
  numericSetting('advanced.acquisition.rgb_adc.offset.green', 'ADC Green Offset', 'Acquisition', 'RGB/component green offset', 0x1a30, 'int16', {
    min: -100,
    max: 100,
    step: 1,
    showPositiveSign: true,
  }),
  numericSetting('advanced.acquisition.rgb_adc.offset.blue', 'ADC Blue Offset', 'Acquisition', 'RGB/component blue offset', 0x1b30, 'int16', {
    min: -100,
    max: 100,
    step: 1,
    showPositiveSign: true,
  }),
  enumSetting('advanced.acquisition.rgb_adc.sampling.anti_alias_lpf', 'Anti-Alias LPF', 'Acquisition', 'RGB/component anti-alias LPF', 0x0ffa, [
    option('Auto (16MHz)', 0),
    option('9 MHz (Strong)', 1),
    option('16 MHz (Med)', 2),
    option('35 MHz (Light)', 3),
    option('95 MHz (Min.)', 4),
    option('Off', 5),
  ]),
  enumSetting('advanced.acquisition.rgb_adc.sampling.decimation_factor', 'Decimation Factor', 'Acquisition', 'RGB/component decimation factor', 0x0e7a, [
    option('Off', 0),
    ...Array.from({ length: 63 }, (_, index) => option(String(index + 2), index + 1)),
  ]),
  {
    path: 'advanced.acquisition.rgb_adc.sampling.decimation_phase',
    label: 'Decimation Phase',
    category: 'Acquisition',
    description: 'RGB/component decimation phase',
    byteRanges: [{ address: 0x0efa, length: 1 }],
    type: 'uint8',
    writable: true,
    min: 1,
    max: 64,
    step: 1,
    offset: 1,
  },
  numericSetting('advanced.acquisition.rgb_adc.sampling.samples_per_line', 'Samples per Line', 'Acquisition', 'RGB/component samples per line', 0x0d7c, 'uint16', {
    min: 1,
    max: 4095,
    step: 1,
  }),
  bitSetting('advanced.acquisition.rgb_adc.sampling.native_sampling', 'Native Sampling', 'Acquisition', 'RGB/component native sampling', rt4kFieldOffset('native_sample_en')),
  numericSetting('advanced.acquisition.rgb_adc.sampling.sub_phase', 'Sub-Phase', 'Acquisition', 'RGB/component sampling sub-phase', 0x0f7a, 'uint8', {
    min: 0,
    max: 348.75,
    step: 11.25,
    scale: 11.25,
    precision: 2,
    units: 'deg',
    preserveTrailingZeros: true,
  }),
  numericSetting('advanced.acquisition.rgb_adc.sync.sog_threshold', 'SoG Threshold', 'Acquisition', 'RGB/component sync-on-green threshold', 0x107a, 'int8', {
    min: 0,
    max: 350,
    step: 11.29,
    scale: 350 / 31,
    offset: (11 * 350) / 31,
    precision: 2,
    units: 'mV',
  }),
  enumSetting('advanced.acquisition.rgb_adc.sync.pre_coast', 'Pre-coast', 'Acquisition', 'RGB/component sync pre-coast', 0x10fa, [
    option('Off', 0),
    ...Array.from({ length: 31 }, (_, index) => option(String(index + 1), index + 1)),
  ]),
  enumSetting('advanced.acquisition.rgb_adc.sync.post_coast', 'Post-coast', 'Acquisition', 'RGB/component sync post-coast', 0x117a, [
    option('Off', 0),
    ...Array.from({ length: 31 }, (_, index) => option(String(index + 1), index + 1)),
  ]),
  bitSetting('advanced.acquisition.rgb_adc.sync.wide_tolerance', 'Wide Tolerance', 'Acquisition', 'RGB/component sync wide tolerance', 0x1924),
  bitSetting('advanced.acquisition.sample_rate_detection.control.auto_decimation_factor', 'Auto Dec. Factor', 'Acquisition', 'Sample rate detection auto decimation factor', 0x1c4c),
  // Live OSD appends the detected phase (e.g. "Auto (1)") or shows "N/A" when unavailable.
  // The phase suffix is runtime status; the saved Auto value is always 1.
  enumSetting('advanced.acquisition.sample_rate_detection.control.auto_decimation_phase', 'Auto Dec. Phase', 'Acquisition', 'Sample rate detection auto decimation phase', 0x1c50, [
    option('Off', 2),
    option('Auto', 1),
  ]),
  enumSetting('advanced.acquisition.sample_rate_detection.control.detection_mode', 'Detection Mode', 'Acquisition', 'Sample rate detection mode', 0x1f00, [
    option('Generic Console', 0),
    option('Saturn', 1),
  ]),
  bitSetting('advanced.acquisition.sample_rate_detection.control.deblur_decimation_8_4', 'De-blur Dec. 8/4', 'Acquisition', 'Sample rate detection 8/4 de-blur decimation', 0x1f40),
  enumSetting('advanced.acquisition.sample_rate_detection.control.enable_edtv', 'Enable EDTV', 'Acquisition', 'Sample rate detection EDTV enable', 0x240c, [
    option('Off', 0),
    option('On', 1),
    option('On+VGA', 2),
  ]),
  enumSetting('advanced.acquisition.sample_rate_detection.control.link_scaler_vert', 'Link Scaler/Vert', 'Acquisition', 'Sample rate detection scaler vertical link', 0x241d, [
    option('Off', 1),
    option('On', 0),
  ]),
  // Live OSD shows "Pend. Config" for an enabled mode without a valid loaded preset.
  enumSetting('advanced.acquisition.sample_rate_detection.lumacode.enable', 'Enable', 'Acquisition', 'LumaCode enable mode', rt4kFieldOffset('lumacode_en'), [
    option('Off', 0),
    option('On (AGC)', 1),
    option('On (Manual)', 2),
  ]),
  {
    path: LUMACODE_PRESET_PATH,
    label: 'Load Preset',
    category: 'Acquisition',
    description: 'LumaCode preset file',
    // Live OSD shows "<Select>" when empty and omits the .lmc extension when loaded.
    byteRanges: [{ address: rt4kFieldOffset('lumacode_file'), length: RT4K_FILE_PATH_BYTES }],
    type: 'string',
    writable: true,
  },
  {
    path: 'advanced.acquisition.sample_rate_detection.optimal_sampling.slot_enabled',
    label: 'Mode Enabled',
    category: 'Acquisition',
    description: 'Selected optimal sampling mode enable',
    byteRanges: [{ address: rt4kArrayElementOffset('opt_dec_factor_en', 0), length: 1 }],
    type: 'bit',
    writable: true,
  },
  bitSetting('advanced.acquisition.sample_rate_detection.progressive.enable_1_10', 'Progressive Enable 1/10', 'Acquisition', 'Sample rate progressive detection 1/10 enable', 0x1c2c),
  bitSetting('advanced.acquisition.sample_rate_detection.progressive.enable_1_8', 'Progressive Enable 1/8', 'Acquisition', 'Sample rate progressive detection 1/8 enable', 0x1c2d),
  bitSetting('advanced.acquisition.sample_rate_detection.progressive.enable_1_7', 'Progressive Enable 1/7', 'Acquisition', 'Sample rate progressive detection 1/7 enable', 0x1c2e),
  bitSetting('advanced.acquisition.sample_rate_detection.progressive.enable_1_6', 'Progressive Enable 1/6', 'Acquisition', 'Sample rate progressive detection 1/6 enable', 0x1c2f),
  bitSetting('advanced.acquisition.sample_rate_detection.progressive.enable_1_5', 'Progressive Enable 1/5', 'Acquisition', 'Sample rate progressive detection 1/5 enable', 0x1c30),
  bitSetting('advanced.acquisition.sample_rate_detection.progressive.enable_1_4', 'Progressive Enable 1/4', 'Acquisition', 'Sample rate progressive detection 1/4 enable', 0x1c31),
  bitSetting('advanced.acquisition.sample_rate_detection.interlaced.enable_1_10', 'Interlaced Enable 1/10', 'Acquisition', 'Sample rate interlaced detection 1/10 enable', 0x1c32),
  bitSetting('advanced.acquisition.sample_rate_detection.interlaced.enable_1_8', 'Interlaced Enable 1/8', 'Acquisition', 'Sample rate interlaced detection 1/8 enable', 0x1c33),
  bitSetting('advanced.acquisition.sample_rate_detection.interlaced.enable_1_7', 'Interlaced Enable 1/7', 'Acquisition', 'Sample rate interlaced detection 1/7 enable', 0x1c34),
  bitSetting('advanced.acquisition.sample_rate_detection.interlaced.enable_1_6', 'Interlaced Enable 1/6', 'Acquisition', 'Sample rate interlaced detection 1/6 enable', 0x1c35),
  bitSetting('advanced.acquisition.sample_rate_detection.interlaced.enable_1_5', 'Interlaced Enable 1/5', 'Acquisition', 'Sample rate interlaced detection 1/5 enable', 0x1c36),
  bitSetting('advanced.acquisition.sample_rate_detection.interlaced.enable_1_4', 'Interlaced Enable 1/4', 'Acquisition', 'Sample rate interlaced detection 1/4 enable', 0x1c37),
  enumSetting('advanced.acquisition.sdp.two_d.yc_filter', '2D Y/C Filter', 'Acquisition', 'SDP 2D Y/C filter', 0x0d58, [
    option('2D Adaptive', 0),
    option('2D Fixed', 1),
    option('Notch', 2),
  ]),
  enumSetting('advanced.acquisition.sdp.two_d.bandwidth', '2D Bandwidth', 'Acquisition', 'SDP 2D bandwidth', 0x0d5c, [
    option('High', 0),
    option('Low', 1),
  ]),
  enumSetting('advanced.acquisition.sdp.two_d.chroma_bandwidth', 'Chroma Bandwidth', 'Acquisition', 'SDP chroma bandwidth', 0x0d60, [
    option('High', 0),
    option('Medium', 1),
    option('Low', 2),
  ]),
  numericSetting('advanced.acquisition.sdp.two_d.sharpness', 'Sharpness', 'Acquisition', 'SDP 2D sharpness', 0x0d64, 'uint8', {
    min: 0,
    max: 15,
    step: 1,
  }),
  numericSetting('advanced.acquisition.sdp.two_d.ctie', 'CTIE', 'Acquisition', 'SDP 2D CTIE', 0x0d68, 'uint8', {
    min: 0,
    max: 3,
    step: 1,
  }),
  enumSetting('advanced.acquisition.sdp.sync.hlock_speed', 'H-Lock Speed', 'Acquisition', 'SDP horizontal lock speed', 0x0d6c, [
    option('Auto', 0),
    option('Slow', 1),
    option('Medium', 2),
    option('Fast', 3),
  ]),
  enumSetting('advanced.acquisition.sdp.sync.standard', 'Standard', 'Acquisition', 'SDP sync standard', 0x0d70, [
    option('Auto', 0),
    option('NTSC', 1),
    option('PAL', 2),
    option('SECAM', 3),
    option('NTSC-443', 4),
    option('PAL-M', 5),
    option('PAL-N', 6),
    option('PAL-60', 7),
    option('NTSC-50', 8),
  ]),
  bitSetting('advanced.acquisition.sdp.gain_balance.blue_only', 'Blue Only', 'Acquisition', 'SDP blue-only mode', 0x0d74),
  numericSetting('advanced.acquisition.sdp.gain_balance.brightness', 'Brightness', 'Acquisition', 'SDP brightness', 0x0d44, 'int32', {
    min: -100,
    max: 100,
    step: 1,
    showPositiveSign: true,
  }),
  numericSetting('advanced.acquisition.sdp.gain_balance.contrast', 'Contrast', 'Acquisition', 'SDP contrast', 0x0d48, 'int32', {
    min: -80,
    max: 120,
    step: 1,
    offset: 20,
    showPositiveSign: true,
  }),
  numericSetting('advanced.acquisition.sdp.gain_balance.phase', 'Phase', 'Acquisition', 'SDP phase', 0x0d4c, 'int32', {
    min: -100,
    max: 100,
    step: 1,
    showPositiveSign: true,
  }),
  numericSetting('advanced.acquisition.sdp.gain_balance.chroma', 'Chroma', 'Acquisition', 'SDP chroma', 0x0d50, 'int32', {
    min: -100,
    max: 100,
    step: 1,
    showPositiveSign: true,
  }),
  enumSetting('advanced.acquisition.sdp.gain_balance.setup', 'Setup', 'Acquisition', 'SDP setup level', 0x0d54, [
    option('0 IRE', 0),
    option('7.5 IRE', 1),
  ]),
  enumSetting('advanced.acquisition.sdp.two_d.pal_delay_line', 'PAL Delay Line', 'Acquisition', 'SDP PAL delay line', 0x1f28, [
    option('On', 0),
    option('Off', 1),
  ]),
  bitSetting('advanced.acquisition.sdp.three_d.comb_enable', '3D Comb Enable', 'Acquisition', 'SDP 3D comb enable', 0x1f1c),
  enumSetting('advanced.acquisition.sdp.three_d.noise_threshold', '3D Noise Threshold', 'Acquisition', 'SDP 3D noise threshold', 0x1f20, [
    option('Default', 0),
    option('Low', 1),
    option('Medium', 2),
    option('High', 3),
  ]),
  numericSetting('advanced.acquisition.sdp.enhanced_svideo.chroma_shift', 'Chroma Shift', 'Acquisition', 'Enhanced S-video chroma shift', 0x1918, 'int32', {
    min: -100,
    max: 100,
    step: 1,
  }),
  {
    path: 'advanced.acquisition.sdp.gain_balance.expand_gamut',
    label: 'Expand Gamut',
    category: 'Acquisition',
    description: 'SDP expand gamut',
    byteRanges: [
      { address: 0x1f54, length: 1 },
      { address: 0x1f58, length: 1 },
    ],
    type: 'enum',
    writable: true,
    options: [
      option('Off', [0, 0]),
      option('On', [1, 1]),
    ],
  },
  {
    path: 'advanced.effects.mask.enabled',
    label: 'Mask',
    category: 'Processing',
    description: 'Mask processing mode',
    byteRanges: [{ address: 0x008c, length: 1 }],
    type: 'enum',
    writable: true,
    options: [
      { label: 'Off', value: [0] },
      { label: 'Multiply', value: [1] },
      { label: 'Spatial Redist.', value: [2] },
    ],
  },
  {
    path: 'advanced.effects.mask.strength',
    label: 'Mask Strength',
    category: 'Processing',
    description: 'Mask strength',
    byteRanges: [{ address: 0x02a0, length: 4 }],
    type: 'int32',
    writable: true,
    min: -10,
    max: 10,
    step: 1,
  },
  {
    path: 'advanced.effects.mask.path',
    label: 'Mask Path',
    category: 'Processing',
    description: 'Mask bitmap path on the RT4K SD card',
    byteRanges: [{ address: rt4kFieldOffset('crt_mask_file'), length: RT4K_FILE_PATH_BYTES }],
    type: 'string',
    writable: true,
  },
  enumSetting('advanced.effects.scanline.function', 'Scanline Function', 'Processing', 'Scanline function', 0x0080, [
    option('Off', 0),
    option('Exponential', 1),
    option('Gaussian', 2),
    option('Super Gaussian', 3),
    option('Linear', 4),
    option('Box', 5),
    option('E-Conserve', 6),
    option('LCD Mono 1', 7),
    option('LCD Mono 2', 8),
    option('LCD BGR', 9),
    option('LCD RGB', 10),
  ]),
  numericSetting('advanced.effects.scanline.strength', 'Scanline Strength', 'Processing', 'Scanline strength', 0x0084, 'uint8', {
    min: 0,
    max: 99,
    step: 1,
  }),
  numericSetting('advanced.effects.scanline.modulation', 'Scanline Modulation', 'Processing', 'Scanline modulation', 0x0088, 'uint8', {
    min: 0,
    max: 99,
    step: 1,
  }),
  {
    path: 'advanced.effects.scanline.e_conserve_beam_spread',
    label: 'Beam Spread',
    category: 'Processing',
    description: 'E-Conserve scanline beam spread',
    byteRanges: [{ address: rt4kFieldOffset('e_scanline_spread'), length: 2 }],
    type: 'enum',
    writable: true,
    options: [
      option('Minimum', [0, 0]),
      option('Low', [1, 0]),
      option('Medium', [2, 0]),
      option('Wide', [3, 0]),
    ],
  },
  numericSetting('advanced.effects.scanline.red_convergence', 'Red Convergence', 'Processing', 'Scanline red convergence', 0x02a4, 'int32', {
    min: -10,
    max: 10,
    step: 1,
  }),
  numericSetting('advanced.effects.scanline.blue_convergence', 'Blue Convergence', 'Processing', 'Scanline blue convergence', 0x02ac, 'int32', {
    min: -10,
    max: 10,
    step: 1,
  }),
  bitSetting('advanced.effects.scanline.normalize_energy', 'Normalize Energy', 'Processing', 'Scanline normalize energy', 0x02b4),
  enumSetting('advanced.effects.scanline.pseudo_interlace', 'Pseudo Interlace', 'Processing', 'Scanline pseudo interlace', 0x1f04, [
    option('Off', 0),
    option('(2x) 480p->960i', 1),
    option('(1x) 960p->960i', 2),
  ]),
  enumSetting('advanced.effects.interpolation.vertical_kernel', 'Vert. Kernel', 'Processing', 'Vertical interpolation kernel', 0x0d15, [
    option('Bilinear Sharp', 0),
    option('Bilinear Med.', 1),
    option('Bilinear Std.', 2),
    option('Bilinear Soft', 3),
    option('Cubic', 4),
    option('Lanczos2', 5),
    option('Lanczos3', 6),
    option('Nearest Neighbor', 7),
  ]),
  enumSetting('advanced.effects.interpolation.horizontal_kernel', 'Hori. Kernel', 'Processing', 'Horizontal interpolation kernel', 0x0c95, [
    option('Bilinear Sharp', 0),
    option('Bilinear Med.', 1),
    option('Bilinear Std.', 2),
    option('Bilinear Soft', 3),
    option('Cubic', 4),
    option('Lanczos2', 5),
    option('Lanczos3', 6),
    option('Nearest Neighbor', 7),
  ]),
  enumSetting('advanced.effects.interpolation.anti_ringing', 'Anti-Ringing', 'Processing', 'Interpolation anti-ringing', 0x1e94, [
    option('On', 0),
    option('Off', 1),
  ]),
  enumSetting('advanced.effects.interpolation.linear_light', 'Linear Light', 'Processing', 'Interpolation linear light', 0x1ed4, [
    option('On', 0),
    option('Off', 1),
  ]),
  enumSetting('advanced.effects.horizontal_blur.function', 'Horizontal Blur', 'Processing', 'Horizontal blur function', 0x02b8, [
    option('Off', 0),
    option('RC Lowpass', 1),
    option('RC Peaking', 2),
    option('Gaussian', 3),
    option('Butterworth', 4),
    option('Elliptic', 5),
    option('Notch', 6),
    option('Notch Wide', 7),
    option('Notch + Low', 8),
  ]),
  numericSetting('advanced.effects.horizontal_blur.cutoff_frequency', 'Cut-Off Freq', 'Processing', 'Horizontal blur cut-off frequency', 0x02bc, 'uint32', {
    min: 0.5,
    max: 16.25,
    step: 0.05,
    scale: 0.05,
    offset: 0.5,
    precision: 2,
    units: 'MHz',
    preserveTrailingZeros: true,
  }),
  enumSetting('advanced.effects.smoothing.algorithm', 'Smoothing Algorithm', 'Processing', 'Smoothing algorithm', 0x1f14, [
    option('Off', 0),
    option('XBR Level 1', 1),
    option('XBR Level 2', 2),
  ]),
  enumSetting('advanced.effects.smoothing.noise_threshold', 'Smoothing Noise Threshold', 'Processing', 'Smoothing noise threshold', 0x1f18, [
    option('Off', 0),
    option('Low', 1),
    option('Medium', 2),
    option('High', 3),
  ]),
  numericSetting('advanced.color.gamma_pq.input_factor', 'Input Factor', 'Processing', 'Gamma/PQ input factor', 0x02c0, 'int32', {
    min: 0.1,
    max: 5,
    step: 0.01,
    scale: 0.01,
    offset: 1,
    precision: 2,
    preserveTrailingZeros: true,
  }),
  numericSetting('advanced.color.gamma_pq.output_factor', 'Output Factor', 'Processing', 'Gamma/PQ output factor', 0x02c8, 'int32', {
    min: 0.1,
    max: 5,
    step: 0.01,
    scale: 0.01,
    offset: 1,
    precision: 2,
    preserveTrailingZeros: true,
  }),
  numericSetting('advanced.color.gamma_pq.smpte_2084_pq', 'SMPTE 2084 PQ', 'Processing', 'SMPTE 2084 PQ peak luminance', 0x02cc, 'int32', {
    min: 250,
    max: 10000,
    step: 0.1,
    precision: 1,
    units: 'nits',
  }),
  numericSetting('advanced.color.gamma_pq.input_lift', 'Input Lift', 'Processing', 'Gamma/PQ input lift', 0x1ecc, 'float32', {
    min: -1,
    max: 1,
    step: 0.01,
    precision: 2,
    preserveTrailingZeros: true,
  }),
  numericSetting('advanced.color.gamma_pq.input_gain', 'Input Gain', 'Processing', 'Gamma/PQ input gain', 0x1ed0, 'float32', {
    min: 0,
    max: 10,
    step: 0.01,
    offset: 1,
    precision: 2,
    preserveTrailingZeros: true,
  }),
  enumSetting('advanced.color.advanced.transfer_function', 'Transfer Function', 'Processing', 'Color transfer function', 0x1ed8, [
    option('sRGB 0.055', 0),
    option('Rec.601/709 0.099', 1),
    option('SMPTE 240M 0.1115', 2),
    option('Gamma', 3),
  ]),
  numericSetting('advanced.color.csc.saturation', 'Saturation', 'Processing', 'Color space conversion saturation', 0x1ee4, 'float32', {
    min: -1,
    max: 1,
    step: 0.01,
    precision: 2,
    preserveTrailingZeros: true,
    showPositiveSign: true,
  }),
  bitSetting('advanced.color.csc.custom_matrix', 'Custom Matrix', 'Processing', 'Color space conversion custom matrix', 0x1ec4),
  bitSetting('advanced.color.csc.prevent_clipping', 'Prevent Clipping', 'Processing', 'Color space conversion prevent clipping', 0x1ef0),
  numericSetting('advanced.color.rgb_to_xyz.xo_rin', 'Xo[Rin]', 'Processing', 'Input RGB to XYZ matrix X row red coefficient', 0x1ea0, 'float32', {
    min: -5,
    max: 5,
    step: 0.001,
    precision: 5,
    preserveTrailingZeros: true,
    showPositiveSign: true,
  }),
  numericSetting('advanced.color.rgb_to_xyz.xo_gin', 'Xo[Gin]', 'Processing', 'Input RGB to XYZ matrix X row green coefficient', 0x1ea4, 'float32', {
    min: -5,
    max: 5,
    step: 0.001,
    precision: 5,
    preserveTrailingZeros: true,
    showPositiveSign: true,
  }),
  numericSetting('advanced.color.rgb_to_xyz.xo_bin', 'Xo[Bin]', 'Processing', 'Input RGB to XYZ matrix X row blue coefficient', 0x1ea8, 'float32', {
    min: -5,
    max: 5,
    step: 0.001,
    precision: 5,
    preserveTrailingZeros: true,
    showPositiveSign: true,
  }),
  numericSetting('advanced.color.rgb_to_xyz.yo_rin', 'Yo[Rin]', 'Processing', 'Input RGB to XYZ matrix Y row red coefficient', 0x1eac, 'float32', {
    min: -5,
    max: 5,
    step: 0.001,
    precision: 5,
    preserveTrailingZeros: true,
    showPositiveSign: true,
  }),
  numericSetting('advanced.color.rgb_to_xyz.yo_gin', 'Yo[Gin]', 'Processing', 'Input RGB to XYZ matrix Y row green coefficient', 0x1eb0, 'float32', {
    min: -5,
    max: 5,
    step: 0.001,
    precision: 5,
    preserveTrailingZeros: true,
    showPositiveSign: true,
  }),
  numericSetting('advanced.color.rgb_to_xyz.yo_bin', 'Yo[Bin]', 'Processing', 'Input RGB to XYZ matrix Y row blue coefficient', 0x1eb4, 'float32', {
    min: -5,
    max: 5,
    step: 0.001,
    precision: 5,
    preserveTrailingZeros: true,
    showPositiveSign: true,
  }),
  numericSetting('advanced.color.rgb_to_xyz.zo_rin', 'Zo[Rin]', 'Processing', 'Input RGB to XYZ matrix Z row red coefficient', 0x1eb8, 'float32', {
    min: -5,
    max: 5,
    step: 0.001,
    precision: 5,
    preserveTrailingZeros: true,
    showPositiveSign: true,
  }),
  numericSetting('advanced.color.rgb_to_xyz.zo_gin', 'Zo[Gin]', 'Processing', 'Input RGB to XYZ matrix Z row green coefficient', 0x1ebc, 'float32', {
    min: -5,
    max: 5,
    step: 0.001,
    precision: 5,
    preserveTrailingZeros: true,
    showPositiveSign: true,
  }),
  numericSetting('advanced.color.rgb_to_xyz.zo_bin', 'Zo[Bin]', 'Processing', 'Input RGB to XYZ matrix Z row blue coefficient', 0x1ec0, 'float32', {
    min: -5,
    max: 5,
    step: 0.001,
    precision: 5,
    preserveTrailingZeros: true,
    showPositiveSign: true,
  }),
  enumSetting('advanced.color.advanced.bit_crush', 'Bit Crush', 'Processing', 'Removed output bits', 0x1928, [
    option('Off', 0),
    option('1 bit Removed', 1),
    option('2 bits Removed', 2),
    option('3 bits Removed', 3),
    option('4 bits Removed', 4),
    option('5 bits Removed', 5),
    option('6 bits Removed', 6),
    option('7 bits Removed', 7),
  ]),
  bitSetting('advanced.color.advanced.dithering', 'Dithering', 'Processing', 'Output dithering', 0x1f0c),
  {
    path: 'advanced.scaling.input_crop.rotate',
    label: 'RoTATE (Beta)',
    category: 'Processing',
    description: 'Input crop rotate',
    byteRanges: [
      { address: 0x1868, length: 1 },
      { address: 0x186c, length: 1 },
    ],
    type: 'enum',
    writable: true,
    options: [
      option('Off', [0, 0]),
      option('90 Deg Right', [1, 1]),
      option('90 Deg Left', [2, 1]),
    ],
  },
  numericSetting('advanced.scaling.input_crop.top_trim', 'Top Trim', 'Processing', 'Input crop top trim', 0x0686, 'int16', {
    min: -4096,
    max: 4096,
    step: 1,
    showPositiveSign: true,
  }),
  numericSetting('advanced.scaling.input_crop.bottom_trim', 'Bottom Trim', 'Processing', 'Input crop bottom trim', 0x0786, 'int16', {
    min: -4096,
    max: 4096,
    step: 1,
    showPositiveSign: true,
  }),
  numericSetting('advanced.scaling.input_crop.left_trim', 'Left Trim', 'Processing', 'Input crop left trim', 0x0586, 'int16', {
    min: -4096,
    max: 4096,
    step: 1,
    showPositiveSign: true,
  }),
  numericSetting('advanced.scaling.input_crop.right_trim', 'Right Trim', 'Processing', 'Input crop right trim', 0x0486, 'int16', {
    min: -4096,
    max: 4096,
    step: 1,
    showPositiveSign: true,
  }),
  enumSetting('advanced.scaling.input_crop.vertical_prescale', 'Vert. Pre-Scale', 'Processing', 'Input crop vertical pre-scale', 0x0815, [
    option('Off', 0),
    ...Array.from({ length: 30 }, (_, index) => option(`1/${index + 2}`, index + 1)),
  ]),
  enumSetting('advanced.scaling.scaler.aspect_correction', 'Aspect Correction', 'Processing', 'Scaler aspect correction', 0x0826, [
    option('1:1 (Sq. Pixel)', 0),
    option('16:9 (PAR)', 1),
    option('4:3 (PAR)', 2),
  ]),
  enumSetting('advanced.scaling.scaler.scaling_mode', 'Scaling Mode', 'Processing', 'Scaler scaling mode', 0x0915, [
    option('Auto Fill', 0),
    option('Proportional', 1),
    option('Free-Form', 2),
    option('Auto Fill Integer', 3),
    option('Auto Fill Int+', 4),
    option('Auto Fill Int HV', 5),
    option('Auto Fill Int HV+', 6),
  ]),
  numericSetting('advanced.scaling.scaler.vertical_pan', 'Vert. Pan', 'Processing', 'Scaler vertical pan for integer overscan modes', rt4kArrayElementOffset('scaler_vert_pan', 1), 'int8', {
    min: -64,
    max: 64,
    step: 1,
    showPositiveSign: true,
    showPositiveSignForZero: false,
  }),
  numericSetting('advanced.scaling.scaler.horizontal_factor', 'Hori. Factor', 'Processing', 'Scaler horizontal factor', 0x0b06, 'int16', {
    min: 1,
    step: 0.001,
    scale: 1 / 1920,
    offset: 2,
    precision: 3,
    units: 'x',
  }),
  numericSetting('advanced.scaling.scaler.vertical_factor', 'Vert. Factor', 'Processing', 'Scaler vertical factor', 0x0c06, 'int16', {
    min: 1,
    step: 0.004,
    scale: 1 / 1080,
    offset: 2,
    precision: 3,
    units: 'x',
  }),
  enumSetting('advanced.scaling.scaler.buffer_length', 'Buffer Length', 'Processing', 'Scaler buffer length', 0x0d24, [
    option('Min. Lag', 0),
    option('1/2 Frame', 1),
    option('1 Frame', 2),
  ]),
  bitSetting('advanced.scaling.scaler.blank_resolution_change', 'Blank Res. Change', 'Processing', 'Blank during resolution change', 0x1f24),
  numericSetting('advanced.scaling.masking_color.red', 'Masking Red', 'Processing', 'Masking color red channel', 0x1854, 'uint32', {
    min: 0,
    max: 31,
    step: 1,
  }),
  numericSetting('advanced.scaling.masking_color.green', 'Masking Green', 'Processing', 'Masking color green channel', 0x1858, 'uint32', {
    min: 0,
    max: 31,
    step: 1,
  }),
  numericSetting('advanced.scaling.masking_color.blue', 'Masking Blue', 'Processing', 'Masking color blue channel', 0x185c, 'int32', {
    min: 0,
    max: 31,
    step: 1,
    offset: 31,
  }),
  enumSetting('advanced.scaling.masking_color.show', 'Masking Color Show', 'Processing', 'Masking color visibility', 0x191c, [
    option('Cropping Only', 0),
    option('Always', 1),
  ]),
  enumSetting('advanced.deinterlacer.mode.algorithm', 'Deinterlacer Algorithm', 'Processing', 'Deinterlacer algorithm', 0x1628, [
    option('Motion Adaptive', 0),
    option('Weave', 1),
    option('Bob', 2),
    option('Linear', 3),
    option('Blend', 4),
    option('CRT Simulation', 5),
    option('LCD Blending', 6),
  ]),
  bitSetting('advanced.deinterlacer.mode.field_inversion', 'Field Inversion', 'Processing', 'Deinterlacer field inversion', 0x162c),
  enumSetting('advanced.deinterlacer.motion_adaptive.sensitivity', 'Motion Sensitivity', 'Processing', 'Motion-adaptive sensitivity', 0x1630, [
    option('Low', 0),
    option('Medium', 1),
    option('High', 2),
    option('Max', 3),
  ]),
  enumSetting('advanced.deinterlacer.motion_adaptive.interpolator_edge_adaptive', 'Motion Interpolator', 'Processing', 'Motion-adaptive interpolation', 0x1634, [
    option('Linear', 0),
    option('Edge Adaptive', 1),
  ]),
  enumSetting('advanced.deinterlacer.motion_adaptive.detector_symmetric', 'Motion Detector', 'Processing', 'Motion-adaptive detector', 0x1638, [
    option('Zero-lag', 0),
    option('Symmetric', 1),
  ]),
  numericSetting('advanced.deinterlacer.bob.offset', 'Bob Offset', 'Processing', 'Bob deinterlacer offset', 0x163c, 'int32', {
    min: -3,
    max: 3,
    step: 1,
    showPositiveSign: true,
  }),
  enumSetting('advanced.deinterlacer.film.telecine', 'Inverse Telecine', 'Processing', 'Film inverse telecine mode', 0x1870, [
    option('Off', 0),
    option('3:2', 1),
    option('2:2', 2),
  ]),
  bitSetting('advanced.deinterlacer.film.dejudder_24hz', 'Dejudder 24 Hz', 'Processing', 'Film dejudder 24 Hz', 0x1874),
  numericSetting('advanced.deinterlacer.cadence.motion_digital', 'Cadence Motion Digital', 'Processing', 'Digital cadence motion threshold', 0x1878, 'int32', {
    min: 0,
    max: 500,
    step: 1,
    offset: 2,
  }),
  numericSetting('advanced.deinterlacer.cadence.motion_component', 'Cadence Motion CP', 'Processing', 'Component cadence motion threshold', 0x187c, 'int32', {
    min: 0,
    max: 500,
    step: 1,
    offset: 50,
  }),
  numericSetting('advanced.deinterlacer.cadence.motion_sdp', 'Cadence Motion SDP', 'Processing', 'SDP cadence motion threshold', 0x1880, 'int32', {
    min: 0,
    max: 500,
    step: 1,
    offset: 150,
  }),
  numericSetting('advanced.deinterlacer.cadence.threshold', 'Cadence Threshold', 'Processing', 'Cadence detection threshold', 0x1884, 'int32', {
    min: 1,
    max: 20,
    step: 1,
    offset: 3,
  }),
  numericSetting('advanced.deinterlacer.motion_adaptive.noise_threshold', 'Motion Noise Threshold', 'Processing', 'Motion-adaptive noise threshold', 0x1920, 'uint8', {
    min: 0,
    max: 63,
    step: 1,
  }),
  bitSetting('advanced.deinterlacer.mode.smooth_ip_switch', 'Smooth I/P Switch', 'Processing', 'Deinterlacer smooth I/P switch', 0x1f4c),
  enumSetting('advanced.bfi.color.blending_mode', 'BFI Blending Mode', 'Processing', 'Black frame insertion color blending mode', 0x1908, [
    option('Solid', 0),
    option('Alpha', 1),
    option('Solid Color', 2),
    option('Alpha Color', 3),
    option('CRT Beam', 4),
    option('Variable MPRT', 5),
  ]),
  numericSetting('advanced.bfi.color.red', 'BFI Red', 'Processing', 'Black frame insertion red channel', 0x190c, 'uint8', {
    min: 0,
    max: 11,
    step: 1,
  }),
  numericSetting('advanced.bfi.color.green', 'BFI Green', 'Processing', 'Black frame insertion green channel', 0x1910, 'uint8', {
    min: 0,
    max: 11,
    step: 1,
  }),
  numericSetting('advanced.bfi.color.blue', 'BFI Blue', 'Processing', 'Black frame insertion blue channel', 0x1914, 'uint8', {
    min: 0,
    max: 11,
    step: 1,
  }),
  enumSetting('advanced.bfi.control.lcd_saver', 'LCD Saver', 'Processing', 'BFI LCD saver timer', 0x0364, [
    option('1 min.', 0),
    option('5 min.', 1),
    option('10 min.', 2),
    option('Off', 3),
  ]),
  enumSetting('advanced.bfi.control.strobe', 'BFI Strobe', 'Processing', 'Black frame insertion strobe count', 0x02e8, [
    option('Off', 0),
    ...Array.from({ length: 16 }, (_, index) => option(String(index + 1), index + 1)),
  ]),
  enumSetting('advanced.bfi.control.blur', 'BFI Blur', 'Processing', 'Black frame insertion blur', 0x0328, [
    option('Off', 0),
    ...Array.from({ length: 16 }, (_, index) => option(String(index + 1), index + 1)),
  ]),
  enumSetting('advanced.bfi.control.min_bfi_limit', 'Min. BFI Limit', 'Processing', 'Minimum BFI limit', 0x1ef4, [
    option('On', 0),
    option('Off', 1),
  ]),
  numericSetting('advanced.bfi.variable_mprt.gain', 'MPRT Gain', 'Processing', 'Variable MPRT gain', 0x1f5c, 'int32', {
    min: -10,
    max: 10,
    step: 1,
  }),
  enumSetting('advanced.bfi.rolling.beam_steepness', 'Beam Steepness', 'Processing', 'Rolling BFI beam steepness', 0x1f3c, [
    option('Off', 0),
    option('1', 1),
    option('2', 2),
    option('3', 3),
    option('4', 4),
    option('5', 5),
    option('6', 6),
  ]),
  enumSetting('advanced.bfi.rolling.phosphor_glow', 'Phosphor Glow', 'Processing', 'Rolling BFI phosphor glow', 0x1f3e, [
    option('Off', 0),
    option('1', 1),
    option('2', 2),
    option('3', 3),
    option('4', 4),
    option('5', 5),
    option('6', 6),
    option('7', 7),
    option('8', 8),
    option('9', 9),
    option('10', 10),
    option('11', 11),
    option('12', 12),
  ]),
  {
    path: 'advanced.system.osd_firmware.banner_image.load_banner',
    label: 'Banner Image',
    category: 'System',
    description: 'Banner bitmap path on the RT4K SD card',
    byteRanges: [{ address: rt4kFieldOffset('osd_banner_image_file'), length: RT4K_FILE_PATH_BYTES }],
    type: 'string',
    writable: true,
  },
  {
    path: 'advanced.system.osd_firmware.on_screen_display.auto_off',
    label: 'OSD Auto-Off',
    category: 'System',
    description: 'On-screen display auto-off timer',
    byteRanges: [{ address: 0x1850, length: 1 }],
    type: 'enum',
    writable: true,
    options: [
      { label: 'Off', value: [0] },
      { label: '10sec', value: [1] },
      { label: '20sec', value: [2] },
      { label: '30sec', value: [3] },
      { label: '40sec', value: [4] },
      { label: '50sec', value: [5] },
      { label: '60sec', value: [6] },
      { label: '70sec', value: [7] },
      { label: '80sec', value: [8] },
      { label: '90sec', value: [9] },
      { label: '100sec', value: [10] },
    ],
  },
  {
    path: 'advanced.system.osd_firmware.on_screen_display.position',
    label: 'OSD Position',
    category: 'System',
    description: 'On-screen display position',
    byteRanges: [{ address: 0x184c, length: 1 }],
    type: 'enum',
    writable: true,
    options: [
      { label: 'Left', value: [0] },
      { label: 'Center', value: [1] },
      { label: 'Right', value: [2] },
    ],
  },
  {
    path: 'advanced.system.osd_firmware.on_screen_display.enable_debug_osd',
    label: 'Debug OSD',
    category: 'System',
    description: 'Debug OSD mode',
    byteRanges: [{ address: 0x1864, length: 1 }],
    type: 'enum',
    writable: true,
    options: [
      { label: 'Off', value: [0] },
      { label: 'Status Pg 1', value: [1] },
      { label: 'Status Pg 2', value: [2] },
      { label: 'Status Pg 3', value: [3] },
      { label: 'Console', value: [4] },
    ],
  },
  {
    path: 'advanced.system.osd_firmware.on_screen_display.hide_input_res',
    label: 'Hide Input Res.',
    category: 'System',
    description: 'Hide input resolution in OSD',
    byteRanges: [{ address: 0x1ee8, length: 1 }],
    type: 'bit',
    writable: true,
  },
];

export const SETTINGS_BY_PATH = new Map(SETTING_DEFINITIONS.map((setting) => [setting.path, setting]));
